import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';

const AuthContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: 24px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 48px;
`;

const AuthHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const AuthTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 8px 0;
`;

const AuthSubtitle = styled.p`
  font-size: 1rem;
  color: #495057;
  margin: 0;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Message = styled.div<{ type: 'success' | 'error' }>`
  color: ${props => props.type === 'success' ? '#51cf66' : '#fa5252'};
  background-color: ${props => props.type === 'success' ? '#f3fcf3' : '#fff5f5'};
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.875rem;
  line-height: 1.4;
`;

const AuthFooter = styled.div`
  margin-top: 24px;
  text-align: center;
  font-size: 0.875rem;
  color: #495057;
  
  a {
    color: #4263eb;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: 'If an account with that email exists, we sent you a reset link. Please check your email.' 
        });
        setEmail(''); // Clear the form
      } else {
        setMessage({ 
          type: 'error', 
          text: data.error || 'An error occurred. Please try again.' 
        });
      }

    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'An error occurred. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContainer>
      <AuthHeader>
        <AuthTitle>Forgot Password</AuthTitle>
        <AuthSubtitle>
          Enter your email address and we'll send you a link to reset your password.
        </AuthSubtitle>
      </AuthHeader>
      
      {message && (
        <Message type={message.type}>
          {message.text}
        </Message>
      )}
      
      <Form onSubmit={handleSubmit}>
        <Input
          label="Email"
          type="email"
          placeholder="Enter your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        
        <Button
          type="submit"
          variant="primary"
          isFullWidth
          isLoading={loading}
        >
          {loading ? 'Sending...' : 'Send Reset Link'}
        </Button>
      </Form>
      
      <AuthFooter>
        Remember your password? <Link to="/login">Back to Login</Link>
      </AuthFooter>
    </AuthContainer>
  );
};

export default ForgotPassword;